import { type FC, createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { DefaultConfig } from './_LayoutConfig';
import {
  setLayoutIntoLocalStorage,
  getEmptyCssClasses,
  getEmptyCSSVariables,
  getEmptyHTMLAttributes,
  LayoutSetup,
} from './_LayoutSetup';
import {
  type ILayout,
  type ILayoutCSSVariables,
  type ILayoutCSSClasses,
  type ILayoutHTMLAttributes,
  type LayoutType,
  type ToolbarType,
} from './_Models';
import { type WithChildren } from '../../helpers';
import { useAuth } from '@/app/modules/auth/core/Auth';
import { getLayoutConfigForUserType } from './_LayoutConfigResolver';
import { type ContactType } from '@/app/models/Contact';
import { useLayoutSplashScreen } from './_LayoutSplashScreenProvider';
import { getUserTypeFromJWT } from '@/app/modules/auth/core/_helpers';

export interface LayoutContextModel {
  config: ILayout;
  classes: ILayoutCSSClasses;
  attributes: ILayoutHTMLAttributes;
  cssVariables: ILayoutCSSVariables;
  setLayout: (config: LayoutSetup) => void;
  setLayoutType: (layoutType: LayoutType) => void;
  setToolbarType: (toolbarType: ToolbarType) => void;
}

const LayoutContext = createContext<LayoutContextModel>({
  config: DefaultConfig,
  classes: getEmptyCssClasses(),
  attributes: getEmptyHTMLAttributes(),
  cssVariables: getEmptyCSSVariables(),
  setLayout: (_config: LayoutSetup) => {},
  setLayoutType: (_layoutType: LayoutType) => {},
  setToolbarType: (_toolbarType: ToolbarType) => {},
});

const LayoutProvider: FC<WithChildren> = ({ children }) => {
  const [config, setConfig] = useState(LayoutSetup.config);
  const [classes, setClasses] = useState(LayoutSetup.classes);
  const [attributes, setAttributes] = useState(LayoutSetup.attributes);
  const [cssVariables, setCSSVariables] = useState(LayoutSetup.cssVariables);
  const { showSplashScreen, hideSplashScreen } = useLayoutSplashScreen();
  const { auth } = useAuth();

  const setLayout = useCallback(
    (_themeConfig: Partial<ILayout>) => {
      showSplashScreen();
      const bodyClasses = Array.from(document.body.classList);
      bodyClasses.forEach((cl) => document.body.classList.remove(cl));
      const updatedConfig = LayoutSetup.updatePartialConfig(_themeConfig);
      setConfig(Object.assign({}, updatedConfig));
      setClasses(LayoutSetup.classes);
      setAttributes(LayoutSetup.attributes);
      setCSSVariables(LayoutSetup.cssVariables);
      hideSplashScreen();
    },
    [showSplashScreen, hideSplashScreen],
  );

  const setToolbarType = (toolbarType: ToolbarType) => {
    const updatedConfig = { ...config };
    if (updatedConfig.app?.toolbar) {
      updatedConfig.app.toolbar.layout = toolbarType;
    }

    setLayoutIntoLocalStorage(updatedConfig);
    window.location.reload();
  };

  const setLayoutType = (layoutType: LayoutType) => {
    const updatedLayout = { ...config, layoutType };
    setLayoutIntoLocalStorage(updatedLayout);
    window.location.reload();
  };

  const value: LayoutContextModel = useMemo(
    () => ({
      config,
      classes,
      attributes,
      cssVariables,
      setLayout,
      setLayoutType,
      setToolbarType,
    }),
    [config, classes, attributes, cssVariables],
  );

  // useEffect(() => {
  //  disableSplashScreen()
  // }, [])

  const layoutConfigForUserType = useMemo(() => {
    if (!auth?.token) return undefined;
    const userType = getUserTypeFromJWT(auth.token);
    return getLayoutConfigForUserType(userType);
  }, [auth?.token]);

  useEffect(() => {
    if (layoutConfigForUserType) {
      setLayout(layoutConfigForUserType);
    }
  }, [layoutConfigForUserType, setLayout]);

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export { LayoutContext, LayoutProvider };

export function useLayout() {
  return useContext(LayoutContext);
}
